// src/services/insights.js
// Thin client wrapper around GET /api/admin-insights-summary.
// Admin-only — relies on the Clerk getToken passed in by the caller.
//
// Returns the summary payload (see api/admin-insights-summary.js) or throws an
// Error with the server message / HTTP status. The Insights home calls this on
// open + Refresh + period change; a small client cache keeps re-opens instant
// while an explicit Refresh always bypasses it.

const VALID_PERIODS = ['this-month', '30d', '90d', 'all'];

// `${endpoint}|${period}` → { data, at } — module-scope so it survives tab
// switches within a session.
const _cache = new Map();
const CACHE_TTL_MS = 60 * 1000; // 60s — matches the "Last updated · Refresh" model

export function getCachedInsights(period, kind = 'summary') {
    const hit = _cache.get(`${kind}|${period}`);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.data;
    return null;
}

export function clearInsightsCache() { _cache.clear(); }

// Shared fetch for every admin insights endpoint. Admin-only — relies on the
// Clerk getToken passed in by the caller.
async function fetchInsights(kind, path, period, getToken, { force = false } = {}) {
    const p = VALID_PERIODS.includes(period) ? period : 'this-month';
    if (typeof getToken !== 'function') throw new Error('getToken is required');

    if (!force) {
        const cached = getCachedInsights(p, kind);
        if (cached) return cached;
    }

    const token = await getToken();
    const res = await fetch(`${path}?period=${encodeURIComponent(p)}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        let detail = '';
        try { detail = (await res.json())?.error || ''; } catch (_) { /* defensive */ }
        throw new Error(detail || `HTTP ${res.status}`);
    }
    const data = await res.json();
    _cache.set(`${kind}|${p}`, { data, at: Date.now() });
    return data;
}

export function fetchInsightsSummary(period, getToken, opts) {
    return fetchInsights('summary', '/api/admin-insights-summary', period, getToken, opts);
}

// Business Health drill-in: cancellation reasons + new/lapsed/pending rosters.
// Loaded lazily (only when the drill-in is opened) so the home view never pays
// for the extra Clerk pass.
export function fetchInsightsBusiness(period, getToken, opts) {
    return fetchInsights('business', '/api/admin-insights-business', period, getToken, opts);
}
